<script setup>
import { ref, computed, watch } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import AudienceService from '@/services/AudienceService'
import Alert from '@/components/Atoms/Alert.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import ModalBox from '@/components/modals/ModalBox.vue'
import { MOBILE_SHEET_WIDTH } from './audienceDialog'

// The pinned call-to-action card: one link the host pushes to every viewer's
// panel at once. Setting and removing broadcast immediately, so while one is
// live the strip keeps it in front of the host — it is the loudest thing they
// can do to the room short of clearing it.

const { notify } = useNotification()

const props = defineProps({
	streamId: {
		type: String,
		required: true
	},
	/** The CTA viewers currently see, or null. The parent decides the source. */
	cta: {
		type: Object,
		default: null
	},
	/** Running click total from the stats poll; null until a snapshot lands. */
	clicks: {
		type: Number,
		default: null
	}
})

const emit = defineEmits(['set', 'removed'])

const LABEL_MAX = 80
const BUTTON_MAX = 20
const HEX_PATTERN = /^#[0-9a-fA-F]{6}$/

// The player's default accent, used only to paint the strip's preview pill
// when no colour override is set.
const FALLBACK_ACCENT = '#417dfd'

const modalVisible = ref(false)
const saving = ref(false)
const removing = ref(false)

const draft = ref({ label: '', buttonText: '', url: '', color: '' })

// The worker's rejection, shown verbatim: its https-only refusal is the
// security boundary, and the host should read why the URL was turned down
// rather than a paraphrase of it.
const serverError = ref('')

const validationError = ref('')

const openModal = () => {
	const current = props.cta || {}
	draft.value = {
		label: current.label || '',
		buttonText: current.buttonText || '',
		url: current.url || '',
		color: current.color || ''
	}
	serverError.value = ''
	validationError.value = ''
	modalVisible.value = true
}

// A stale rejection under a corrected form reads as a new one.
watch(draft, () => {
	validationError.value = ''
	serverError.value = ''
}, { deep: true })

const urlValid = computed(() => {
	const value = draft.value.url.trim()
	if (!value) return false

	try {
		return new URL(value).protocol === 'https:'
	} catch (err) {
		return false
	}
})

const colorValid = computed(() => !draft.value.color || HEX_PATTERN.test(draft.value.color))

const validate = () => {
	if (!draft.value.label.trim()) return 'Give the card a label — it is the sentence viewers read.'
	if (!draft.value.buttonText.trim()) return 'Give the button a text, like "Get the deal".'
	if (!urlValid.value) return 'The link must be a full https:// address. Plain http is not allowed.'
	if (!colorValid.value) return 'The colour must be a six-digit hex value like #1a56db, or left empty.'
	return ''
}

const publish = async () => {
	const problem = validate()
	if (problem) {
		validationError.value = problem
		return
	}

	const payload = {
		label: draft.value.label.trim(),
		buttonText: draft.value.buttonText.trim(),
		url: draft.value.url.trim()
	}
	if (draft.value.color) payload.color = draft.value.color

	saving.value = true

	try {
		const res = await AudienceService.setCta(props.streamId, payload)
		modalVisible.value = false
		emit('set', (res && res.cta) || payload)
		notify({ group: 'success', text: 'Call-to-action published to every viewer' })
	} catch (err) {
		serverError.value = err.message || 'Could not publish the call-to-action'
	}

	saving.value = false
}

const remove = async () => {
	removing.value = true

	try {
		await AudienceService.removeCta(props.streamId)
		emit('removed')
		notify({ group: 'success', text: 'Call-to-action removed for every viewer' })
	} catch (err) {
		notify({ group: 'error', text: err.message || 'Could not remove the call-to-action' })
	}

	removing.value = false
}

const liveSince = computed(() => {
	if (!props.cta || !props.cta.setTs) return ''
	return new Date(props.cta.setTs).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
})

const pillColor = computed(() => (props.cta && props.cta.color) || FALLBACK_ACCENT)
</script>

<template>
	<div>
		<!-- Live: loud on purpose. Every viewer is looking at this card. -->
		<div v-if="cta"
			class="flex flex-wrap items-center gap-x-4 gap-y-2 bg-surface-2 ring-1 ring-main rounded-lg px-3 py-2.5">
			<div class="flex items-center gap-x-2 min-w-0 grow">
				<span class="shrink-0 w-2 h-2 rounded-full bg-main animate-pulse" aria-hidden="true"></span>
				<div class="min-w-0">
					<p class="text-sm text-surface-text-1 truncate mb-0" :title="cta.label">
						{{ cta.label }}
					</p>
					<p class="text-tiny text-surface-7 truncate mb-0">
						Live for every viewer<template v-if="liveSince"> since {{ liveSince }}</template> ·
						<a :href="cta.url" target="_blank" rel="noopener noreferrer"
							class="underline underline-offset-2 text-surface-8 hover:text-white break-all">
							{{ cta.url }}
						</a>
					</p>
				</div>
			</div>

			<span class="shrink-0 text-tiny font-medium rounded-md px-2.5 py-1 text-white"
				:style="{ backgroundColor: pillColor }"
				:title="cta.color ? `Button colour ${cta.color}` : 'Button uses the panel accent colour'">
				{{ cta.buttonText }}
			</span>

			<!-- Clicks, not a rate: unique viewers are not tracked, so any
			percentage would have a made-up denominator. -->
			<span class="shrink-0 inline-flex items-baseline gap-x-1 bg-surface-1 rounded-md px-2 py-1"
				title="Total clicks on the button. Viewers are not deduplicated, so this is a count, not a rate.">
				<span class="text-sm font-semibold text-surface-text-2 tabular-nums">{{ clicks === null ? '—' : clicks }}</span>
				<span class="text-tiny text-surface-7">click{{ clicks === 1 ? '' : 's' }}</span>
			</span>

			<div class="flex items-center gap-x-2 shrink-0 ml-auto">
				<FormButton type="secondary" size="sm" :disabled="removing" @click="openModal">
					Edit
				</FormButton>
				<FormButton type="danger" size="sm"
					class="!text-error-light !ring-error-light hover:!bg-surface-1 disabled:!text-surface-7 disabled:!ring-surface-5"
					:is-loading="removing" title="Takes the card away from every viewer immediately"
					@click="remove">
					Remove
				</FormButton>
			</div>
		</div>

		<!-- Idle: one quiet line, the height of the other strips. -->
		<div v-else
			class="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 bg-surface-2 rounded-lg px-3 py-2.5">
			<p class="text-sm text-surface-7 mb-0">
				No call-to-action is live. Pin a link — a product, a signup, a donation page — and every
				viewer sees it above chat.
			</p>
			<FormButton type="secondary" size="sm" @click="openModal">Set call-to-action</FormButton>
		</div>

		<ModalBox v-model="modalVisible" :title="cta ? 'Edit call-to-action' : 'Set call-to-action'"
			size="sm" scroll-body :wrapper-classes="MOBILE_SHEET_WIDTH">
			<div class="px-4 pb-4 sm:px-6 sm:pb-6">
				<p class="text-tiny text-surface-7 mb-4">
					Publishing shows the card to everyone in the room immediately, replacing any card
					already up.
				</p>

				<div class="flex flex-col gap-y-3">
					<div>
						<FormInput v-model="draft.label" label="Label" id="cta-label" :maxlength="LABEL_MAX"
							placeholder="Get 20% off the annual plan today" :disabled="saving" />
						<p class="text-tiny text-surface-7 mt-1 mb-0">
							The sentence on the card. Up to {{ LABEL_MAX }} characters.
						</p>
					</div>

					<div>
						<FormInput v-model="draft.buttonText" label="Button text" id="cta-button"
							:maxlength="BUTTON_MAX" placeholder="Get the deal" :disabled="saving" />
						<p class="text-tiny text-surface-7 mt-1 mb-0">Up to {{ BUTTON_MAX }} characters.</p>
					</div>

					<div>
						<FormInput v-model="draft.url" label="Link" id="cta-url" :maxlength="2048"
							placeholder="https://example.com/offer" :disabled="saving"
							:is-error="!!draft.url.trim() && !urlValid" />
						<p class="text-tiny text-surface-7 mt-1 mb-0">
							Where the button sends viewers. Must be https — the room refuses anything else.
						</p>
					</div>

					<div>
						<label class="block font-medium text-tiny text-surface-text-1 mb-2" for="cta-color-hex">
							Button colour <span class="font-normal text-surface-7">(optional)</span>
						</label>
						<div class="flex items-center gap-x-2">
							<input type="color" aria-label="Pick a button colour" :disabled="saving"
								class="w-9 h-8 rounded border border-surface-4 bg-transparent p-0 cursor-pointer disabled:cursor-not-allowed"
								:value="colorValid && draft.color ? draft.color : FALLBACK_ACCENT"
								@input="draft.color = $event.target.value">
							<input id="cta-color-hex" type="text" spellcheck="false" maxlength="7"
								:disabled="saving" placeholder="Panel accent"
								class="w-[110px] font-mono text-sm bg-surface-2 rounded-md px-2 py-1.5 border"
								:class="colorValid ? 'border-surface-4 text-surface-text-1' : 'border-error text-error'"
								:value="draft.color" @input="draft.color = $event.target.value">
							<button v-if="draft.color" type="button"
								class="text-tiny text-surface-8 hover:text-white underline" :disabled="saving"
								@click="draft.color = ''">
								Use panel accent
							</button>
						</div>
						<p class="text-tiny text-surface-7 mt-1 mb-0">
							Left empty, the button wears the panel's accent colour.
						</p>
					</div>
				</div>

				<Alert v-if="validationError" type="warning" class="mt-4" :text="validationError" />
				<Alert v-if="serverError" type="error" class="mt-4" :text="serverError" />
			</div>

			<template #footer>
				<footer class="flex flex-col gap-2 border-t border-surface-3 p-4 sm:flex-row-reverse sm:p-6">
					<FormButton class="w-full justify-center sm:w-auto" :is-loading="saving" @click="publish">
						{{ cta ? 'Update for everyone' : 'Publish to everyone' }}
					</FormButton>
					<FormButton class="w-full justify-center sm:w-auto" type="secondary" :disabled="saving"
						@click="modalVisible = false">
						Cancel
					</FormButton>
				</footer>
			</template>
		</ModalBox>
	</div>
</template>
